import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout.jsx';
import ListCard from '../components/ListCard.jsx';
import { useLists } from '../hooks/useLists.js';
import { useAuthStore } from '../store/auth.js';

const styles = {
  container: {
    padding: '16px',
    paddingBottom: '88px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: 'var(--tg-theme-hint-color, #8e8e93)',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  emptyText: {
    fontSize: '16px',
    marginBottom: '8px',
  },
  emptySubtext: {
    fontSize: '13px',
    opacity: 0.7,
  },
  fab: {
    position: 'fixed',
    bottom: '24px',
    right: '16px',
    background: 'linear-gradient(135deg, #F05A35 0%, #F4956A 100%)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '28px',
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(240,90,53,0.4)',
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  },
  loadingText: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--tg-theme-hint-color, #8e8e93)',
  },
  errorText: {
    textAlign: 'center',
    padding: '24px',
    color: '#ff3b30',
  },
  modal: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'flex-end',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px 16px 0 0',
    padding: '24px 16px',
    width: '100%',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '700',
    marginBottom: '16px',
    color: '#2D2424',
  },
  input: {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    border: '1px solid #F4956A',
    fontSize: '15px',
    marginBottom: '12px',
    backgroundColor: '#FFF5F0',
    color: '#2D2424',
    outline: 'none',
    boxSizing: 'border-box',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    fontSize: '14px',
    color: 'var(--tg-theme-text-color, #000)',
  },
  btnRow: {
    display: 'flex',
    gap: '12px',
  },
  cancelBtn: {
    flex: 1,
    padding: '12px',
    borderRadius: '10px',
    border: '1px solid #C4A99A',
    backgroundColor: 'transparent',
    fontSize: '15px',
    cursor: 'pointer',
    color: '#2D2424',
  },
  submitBtn: {
    flex: 2,
    padding: '12px',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: 'var(--tg-theme-button-color, #007aff)',
    color: 'var(--tg-theme-button-text-color, #fff)',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

function CreateListModal({ onClose, onCreate }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPersonal, setIsPersonal] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await onCreate({ title: title.trim(), description: description.trim(), is_personal: isPersonal });
      onClose();
    } catch {
      // error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalTitle}>Новый вишлист</div>
        <form onSubmit={handleSubmit}>
          <input
            style={styles.input}
            type="text"
            placeholder="Название *"
            value={title}
            onChange={(e) => setTitle(e.target.value.replace(/https?:\/\/\S+/gi, ''))}
            onKeyDown={(e) => e.stopPropagation()}
            autoFocus
            maxLength={255}
          />
          <input
            style={styles.input}
            type="text"
            placeholder="Описание (по желанию)"
            value={description}
            onChange={(e) => setDescription(e.target.value.replace(/https?:\/\/\S+/gi, ''))}
            onKeyDown={(e) => e.stopPropagation()}
            maxLength={500}
          />
          <label style={styles.checkbox}>
            <input
              type="checkbox"
              checked={isPersonal}
              onChange={(e) => setIsPersonal(e.target.checked)}
            />
            Личный список (приватный)
          </label>
          <div style={styles.btnRow}>
            <button type="button" style={styles.cancelBtn} onClick={onClose}>
              Отмена
            </button>
            <button
              type="submit"
              style={styles.submitBtn}
              disabled={!title.trim() || submitting}
            >
              {submitting ? 'Создание...' : 'Создать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Home() {
  const { lists, loading, error, createList, deleteList, fetchLists } = useLists();
  const { user } = useAuthStore();
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const handleCreate = async (data) => {
    await createList(data);
  };

  const handleDelete = async (id) => {
    try {
      await deleteList(id);
    } catch {
      alert('Не удалось удалить список');
    }
  };

  return (
    <Layout title="Вишлисты" rightAction={
      <button onClick={fetchLists} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--tg-theme-hint-color, #8e8e93)', padding: '4px' }}>
        ↻
      </button>
    }>
      <div style={styles.container}>
        {loading && <div style={styles.loadingText}>Загрузка...</div>}

        {error && (
          <div style={styles.errorText}>
            {error}
            <br />
            <button
              onClick={fetchLists}
              style={{ marginTop: '8px', color: 'var(--tg-theme-link-color, #007aff)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Повторить
            </button>
          </div>
        )}

        {!loading && !error && lists.length === 0 && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🎁</div>
            <div style={styles.emptyText}>Нет вишлистов</div>
            <div style={styles.emptySubtext}>Создай свой первый список желаний</div>
          </div>
        )}

        {!loading && lists.length > 0 && (
          <div>
            {lists.map((list) => (
              <ListCard key={list.id} list={list} onDelete={handleDelete} />
            ))}
          </div>
        )}

      </div>

      {!loading && (
        <button style={styles.fab} onClick={() => setShowModal(true)}>
          + Новый вишлист
        </button>
      )}

      {showModal && (
        <CreateListModal
          onClose={() => setShowModal(false)}
          onCreate={handleCreate}
        />
      )}
    </Layout>
  );
}
